const MOIS_LABELS = [
  'JANVIER', 'FEVRIER', 'MARS', 'AVRIL', 'MAI', 'JUIN',
  'JUILLET', 'AOUT', 'SEPTEMBRE', 'OCTOBRE', 'NOVEMBRE', 'DECEMBRE',
];

const PDC_SLOT_SEPARATOR = ' || ';

function extractSupportLabel(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const separatorPos = raw.lastIndexOf(PDC_SLOT_SEPARATOR);
  const label = separatorPos === -1
    ? raw
    : raw.slice(0, separatorPos).trim();

  const dashIdx = label.indexOf(' — ');
  if (dashIdx === -1) {
    const altDashIdx = label.indexOf(' - ');
    return altDashIdx === -1 ? label.trim() : label.slice(0, altDashIdx).trim();
  }

  return label.slice(0, dashIdx).trim();
}

/** Doit rester aligné avec PlanDeComMeta::FEUILLETON_* (API). */
const FEUILLETON_GROUPS = {
  'Feuilleton 10 Reportages': 10,
  'Feuilleton 6 Reportages': 6,
  'Feuilleton 3 Reportages': 3,
};

const FEUILLETON_ITEM_MAPPING = {
  'Feuilleton 10 Reportages Koikispass multi-diffusion (magazine papier Dble page + web (site Koikispass+RS+NL) + campagne de promotion digitale)': 'Feuilleton 10 Reportages',
  'Feuilleton 10 Reportages Koikispass multi-diffusion (magazine papier 1 page + web (site Koikispass+RS+NL) + campagne de promotion digitale)': 'Feuilleton 10 Reportages',
  'Feuilleton 6 Reportages Koikispass multi-diffusion (magazine papier Dble page + web (site Koikispass+RS+NL) + campagne de promotion digitale)': 'Feuilleton 6 Reportages',
  'Feuilleton 6 Reportages Koikispass multi-diffusion (magazine papier 1 page + web (site Koikispass+RS+NL) + campagne de promotion digitale)': 'Feuilleton 6 Reportages',
  'Feuilleton 3 Reportages Koikispass multi-diffusion (magazine papier Dble page + web (site Koikispass+RS+NL) + campagne de promotion digitale)': 'Feuilleton 3 Reportages',
  'Feuilleton 3 Reportages Koikispass multi-diffusion (magazine papier 1 page + web (site Koikispass+RS+NL) + campagne de promotion digitale)': 'Feuilleton 3 Reportages',
  'REPORTAGE Page 3F': 'Feuilleton 3 Reportages',
  'REPORTAGE Page 6F': 'Feuilleton 6 Reportages',
  'REPORTAGE Page 10F': 'Feuilleton 10 Reportages',
  'REPORTAGE Dble Page 3F': 'Feuilleton 3 Reportages',
  'REPORTAGE Dble Page 6F': 'Feuilleton 6 Reportages',
  'REPORTAGE Dble Page 10F': 'Feuilleton 10 Reportages',
};

function resolveFeuilletonGroup(supportValue) {
  const label = extractSupportLabel(supportValue);
  if (!label) return null;
  if (FEUILLETON_ITEM_MAPPING[label]) return FEUILLETON_ITEM_MAPPING[label];

  const known = Object.keys(FEUILLETON_GROUPS).find((group) => label.startsWith(group));
  if (known) return known;

  const shortMatch = label.match(/REPORTAGE(?:\s+Dble)?\s+Page\s+(3|6|10)F\b/i);
  if (shortMatch) return `Feuilleton ${shortMatch[1]} Reportages`;

  return null;
}

function formatPeriode(ligne) {
  const mois = String(ligne?.mois ?? '').trim();
  const annee = ligne?.annee != null ? String(ligne.annee).trim() : '';
  if (!mois && !annee) return null;
  return `${mois}${annee ? ` ${annee}` : ''}`.trim();
}

/**
 * Alertes locales du chiffrage Koikispass (versions courtes).
 * @param {array} lignes
 */
export const buildCalendrierWarnings = (lignes = []) => {
  /** @type {Record<string, string[]>} */
  const monthsByGroup = Object.fromEntries(
    Object.keys(FEUILLETON_GROUPS).map((group) => [group, []]),
  );

  (lignes ?? []).forEach((ligne) => {
    const periode = formatPeriode(ligne);
    ['format_1', 'format_2', 'format_3'].forEach((field) => {
      const group = resolveFeuilletonGroup(ligne?.[field]);
      if (!group || !periode) return;
      if (!monthsByGroup[group].includes(periode)) {
        monthsByGroup[group].push(periode);
      }
    });
    (Array.isArray(ligne?.supports) ? ligne.supports : []).forEach((item) => {
      const group = resolveFeuilletonGroup(item?.value ?? item);
      if (!group || !periode) return;
      if (!monthsByGroup[group].includes(periode)) {
        monthsByGroup[group].push(periode);
      }
    });
  });

  const warnings = [];
  Object.entries(FEUILLETON_GROUPS).forEach(([group, expected]) => {
    const months = monthsByGroup[group] ?? [];
    const count = months.length;
    if (count === 0 || count >= expected) return;

    const missing = expected - count;
    warnings.push({
      code: `feuilleton_incomplete_${group}`,
      title: group,
      message: `Présent sur ${count} mois (${months.join(', ')}) — il en faut ${expected}.`,
      hint: `Ajoutez-le sur ${missing} mois de plus, ou retirez-le.`,
    });
  });

  return warnings;
};

/** Normalise warnings API (objets ou anciennes chaînes). */
export const normalizePlanDeComWarnings = (warnings) => {
  if (!warnings) return [];

  const list = Array.isArray(warnings)
    ? warnings
    : [
      ...(warnings.duo_trio ?? []),
      ...(warnings.feuilletons ?? []),
      ...(warnings.klub ?? []),
      ...(warnings.planning ?? warnings.planning_sync ?? []),
    ];

  return list.map((item, index) => {
    if (typeof item === 'string') {
      return {
        id: `legacy-${index}`,
        title: 'Point d\'attention',
        message: item,
        hint: null,
      };
    }
    return {
      id: item.code ?? `warning-${index}`,
      title: item.title ?? 'Point d\'attention',
      message: item.message ?? '',
      hint: item.hint ?? null,
    };
  }).filter((w) => w.title || w.message);
};

export { MOIS_LABELS };
